import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getPlatformUsers,
  getUser,
  updateUser,
  createUser,
  deleteUser,
  PLATFORM_ROLES,
} from '../services/adminUserService';
import { getUserRoleMetrics } from '../services/adminUserMetricsService';
import { logActivity } from '../services/adminActivityService';
import AdminAuthService from '../services/adminAuthService';
import { can } from '../utils/adminPermissions';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { USERS_TABLE_ID } from '../lib/appwrite';
import './UsersPage.css';

const formatAddress = (addr) => {
  if (!addr) return '';
  if (Array.isArray(addr)) return addr.filter(Boolean).join(', ');
  return String(addr);
};

const parseAddress = (str) => {
  if (!str || !str.trim()) return [];
  return str.split(',').map((s) => s.trim()).filter(Boolean);
};

const formatCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-TZ', { style: 'decimal' });
};

const getRoleDisplay = (role) => {
  const r = (role || '').toLowerCase();
  const map = { customer: 'Customer', buyer: 'Customer', seller: 'Store Owner', driver: 'Driver' };
  return map[r] || r || '—';
};

const CATEGORIES = [
  { id: '', label: 'All' },
  { id: 'seller', label: 'Store Owners' },
  { id: 'customer', label: 'Customers' },
  { id: 'driver', label: 'Drivers' },
];

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [adminRole, setAdminRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [roleMetrics, setRoleMetrics] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mode, setMode] = useState('view'); // view | edit | create
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const canCreate = adminRole && can(adminRole, 'create', 'users');
  const canUpdate = adminRole && can(adminRole, 'update', 'users');
  const canDelete = adminRole && can(adminRole, 'delete', 'users');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersRes, role] = await Promise.all([
        getPlatformUsers(),
        AdminAuthService.getAdminRole(),
      ]);
      setUsers(usersRes || []);
      setAdminRole(role);
    } catch (err) {
      const msg = err.message || 'Failed to load users';
      const isAuthError =
        typeof msg === 'string' &&
        (msg.toLowerCase().includes('not authorized') || msg.toLowerCase().includes('unauthorized'));
      setError(
        isAuthError
          ? `${msg} Add REACT_APP_APPWRITE_API_KEY to .env for server-level access.`
          : msg
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [USERS_TABLE_ID] });

  const filteredUsers = users.filter((u) => {
    const name = [u.fname, u.lname].filter(Boolean).join(' ').toLowerCase();
    const email = (u.email || '').toLowerCase();
    const phone = (u.phoneNumber || u.phone || '').toLowerCase();
    const q = search.toLowerCase().trim();
    const matchSearch = !q || name.includes(q) || email.includes(q) || phone.includes(q);
    const roleVal = (u.roles || u.role || '').toLowerCase().trim();
    let matchCategory = true;
    if (category) {
      if (category === 'customer') {
        matchCategory = roleVal === 'customer' || roleVal === 'buyer';
      } else {
        matchCategory = roleVal === category;
      }
    }
    return matchSearch && matchCategory;
  });

  const openDetail = (user) => {
    setSelectedUser(user);
    setUserDetails(null);
    setRoleMetrics(null);
    setMode('view');
  };

  const openCreate = () => {
    setSelectedUser(null);
    setUserDetails(null);
    setRoleMetrics(null);
    setMode('create');
    setEditForm({
      fname: '',
      lname: '',
      email: '',
      phoneNumber: '',
      roles: 'customer',
      address: '',
    });
  };

  const startEdit = () => {
    const u = userDetails || selectedUser;
    setEditForm({
      fname: u.fname || '',
      lname: u.lname || '',
      email: u.email || '',
      phoneNumber: u.phoneNumber || u.phone || '',
      roles: (u.roles || u.role || 'customer').toLowerCase(),
      address: formatAddress(u.address),
    });
    setMode('edit');
  };

  useEffect(() => {
    if (!selectedUser?.$id) return;
    let cancelled = false;
    const load = async () => {
      setDetailLoading(true);
      try {
        const [detailsRes, metricsRes] = await Promise.all([
          getUser(selectedUser.$id).catch(() => selectedUser),
          getUserRoleMetrics(selectedUser, selectedUser.roles || selectedUser.role),
        ]);
        if (!cancelled) {
          setUserDetails(detailsRes);
          setRoleMetrics(metricsRes);
        }
      } catch {
        if (!cancelled) setRoleMetrics(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedUser?.$id]);

  const handleSave = async () => {
    if (mode === 'create') {
      setSaving(true);
      try {
        const created = await createUser(editForm);
        await logActivity({
          action: 'create',
          resourceType: 'user',
          resourceId: created.$id,
          details: `Created user ${editForm.fname} ${editForm.lname} (${editForm.roles})`,
        });
        loadData();
        setMode('view');
        setSelectedUser(null);
      } catch (err) {
        setError(err.message || 'Failed to create user');
      } finally {
        setSaving(false);
      }
    } else if (mode === 'edit' && selectedUser?.$id) {
      setSaving(true);
      try {
        const payload = {
          fname: editForm.fname || '',
          lname: editForm.lname || '',
          email: editForm.email || '',
          phoneNumber: editForm.phoneNumber || '',
          roles: (editForm.roles || 'customer').toLowerCase(),
          role: (editForm.roles || 'customer').toLowerCase(),
          address: parseAddress(editForm.address),
        };
        await updateUser(selectedUser.$id, payload);
        await logActivity({
          action: 'update',
          resourceType: 'user',
          resourceId: selectedUser.$id,
          details: `Updated user ${editForm.fname} ${editForm.lname}`,
        });
        setUserDetails((prev) => (prev ? { ...prev, ...payload } : payload));
        setSelectedUser((prev) => (prev ? { ...prev, ...payload } : null));
        setUsers((prev) =>
          prev.map((u) => (u.$id === selectedUser.$id ? { ...u, ...payload } : u))
        );
        setMode('view');
      } catch (err) {
        setError(err.message || 'Failed to update user');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedUser?.$id || !canDelete) return;
    setSaving(true);
    setError(null);
    try {
      await deleteUser(selectedUser.$id);
      await logActivity({
        action: 'delete',
        resourceType: 'user',
        resourceId: selectedUser.$id,
        details: `Deleted user ${selectedUser.fname} ${selectedUser.lname}`,
      });
      setUsers((prev) => prev.filter((u) => u.$id !== selectedUser.$id));
      setSelectedUser(null);
      setShowDeleteConfirm(false);
    } catch (err) {
      const msg = err.message || '';
      const is404 = msg.includes('404') || msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('could not be found');
      if (is404) {
        setUsers((prev) => prev.filter((u) => u.$id !== selectedUser.$id));
        setSelectedUser(null);
        setShowDeleteConfirm(false);
        setError('User was already deleted or no longer exists. List updated.');
      } else {
        setError(msg || 'Failed to delete user');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleViewOrders = () => {
    if (!selectedUser?.$id) return;
    setSelectedUser(null);
    setMode('view');
    navigate('/dashboard/orders', { state: { filterCustomer: selectedUser.$id } });
  };

  const closeModal = () => {
    setSelectedUser(null);
    setMode('view');
    setShowDeleteConfirm(false);
  };

  const isModalOpen = selectedUser || mode === 'create';

  if (loading && users.length === 0) {
    return (
      <div className="users-page-loading">
        <div className="dashboard-spinner" />
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="users-header">
        <div>
          <h1 className="dashboard-title">Users</h1>
          <p className="dashboard-subtitle">
            Platform users: store owners, customers, drivers. Admins are in Admin Users.
          </p>
        </div>
        {canCreate && (
          <button type="button" className="users-btn-create" onClick={openCreate}>
            + Add User
          </button>
        )}
      </div>

      {error && (
        <div className="users-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="users-categories">
        {CATEGORIES.map((c) => (
          <button
            key={c.id || 'all'}
            type="button"
            className={`users-category-btn ${category === c.id ? 'active' : ''}`}
            onClick={() => setCategory(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="users-toolbar">
        <div className="users-search-wrap">
          <img src="/icons/Search.svg" alt="" className="users-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="Search by name, email, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="users-search"
          />
        </div>
        <button type="button" className="users-btn-refresh" onClick={loadData}>
          ⟳ Refresh
        </button>
      </div>

      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={4} className="users-empty">
                  No users found.
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr
                  key={u.$id}
                  className="users-row-clickable"
                  onClick={() => openDetail(u)}
                >
                  <td><strong>{[u.fname, u.lname].filter(Boolean).join(' ') || '—'}</strong></td>
                  <td>{u.email || '—'}</td>
                  <td>{u.phoneNumber || u.phone || '—'}</td>
                  <td>
                    <span className={`users-role users-role-${(u.roles || u.role || '').toLowerCase()}`}>
                      {getRoleDisplay(u.roles || u.role)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="users-modal-overlay" onClick={closeModal} role="dialog" aria-modal="true">
          <div className="users-modal users-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h2>{mode === 'create' ? 'Add User' : mode === 'edit' ? 'Edit User' : 'User Details'}</h2>
              <button type="button" className="users-modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="users-modal-body">
              {mode === 'create' || mode === 'edit' ? (
                <div className="users-modal-form">
                  <div className="users-form-row">
                    <div className="users-form-field">
                      <label>First name</label>
                      <input
                        type="text"
                        value={editForm.fname || ''}
                        onChange={(e) => setEditForm((f) => ({ ...f, fname: e.target.value }))}
                        placeholder="First name"
                      />
                    </div>
                    <div className="users-form-field">
                      <label>Last name</label>
                      <input
                        type="text"
                        value={editForm.lname || ''}
                        onChange={(e) => setEditForm((f) => ({ ...f, lname: e.target.value }))}
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <div className="users-form-field">
                    <label>Email</label>
                    <input
                      type="email"
                      value={editForm.email || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="Email"
                      disabled={mode === 'edit'}
                    />
                  </div>
                  <div className="users-form-field">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={editForm.phoneNumber || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                      placeholder="Phone"
                    />
                  </div>
                  <div className="users-form-field">
                    <label>Role</label>
                    <select
                      value={editForm.roles || 'customer'}
                      onChange={(e) => setEditForm((f) => ({ ...f, roles: e.target.value }))}
                    >
                      {PLATFORM_ROLES.map((r) => (
                        <option key={r} value={r}>{getRoleDisplay(r)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="users-form-field">
                    <label>Address (comma-separated)</label>
                    <input
                      type="text"
                      value={editForm.address || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
                      placeholder="Address line 1, line 2, ..."
                    />
                  </div>
                  <div className="users-modal-actions">
                    <button type="button" className="users-btn-cancel" onClick={() => mode === 'create' ? closeModal() : setMode('view')}>
                      Cancel
                    </button>
                    <button type="button" className="users-btn-save" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : mode === 'create' ? 'Create User' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : detailLoading ? (
                <p className="users-modal-loading">Loading details…</p>
              ) : (
                <>
                  <div className="users-modal-section">
                    <h3>Profile</h3>
                    <div className="users-modal-grid">
                      <div className="users-modal-field">
                        <label>Name</label>
                        <span>{[selectedUser.fname, selectedUser.lname].filter(Boolean).join(' ') || '—'}</span>
                      </div>
                      <div className="users-modal-field">
                        <label>Email</label>
                        <span>
                          <a href={`mailto:${selectedUser.email}`}>{selectedUser.email || '—'}</a>
                        </span>
                      </div>
                      <div className="users-modal-field">
                        <label>Phone</label>
                        <span>
                          <a href={`tel:${selectedUser.phoneNumber || selectedUser.phone}`}>
                            {selectedUser.phoneNumber || selectedUser.phone || '—'}
                          </a>
                        </span>
                      </div>
                      <div className="users-modal-field">
                        <label>Role</label>
                        <span className={`users-role users-role-${(selectedUser.roles || selectedUser.role || '').toLowerCase()}`}>
                          {getRoleDisplay(selectedUser.roles || selectedUser.role)}
                        </span>
                      </div>
                      <div className="users-modal-field">
                        <label>User ID</label>
                        <span className="users-modal-mono">{selectedUser.$id}</span>
                      </div>
                      <div className="users-modal-field users-modal-field-wide">
                        <label>UID</label>
                        <span className="users-modal-mono">{(userDetails || selectedUser).uid || '—'}</span>
                      </div>
                      {((userDetails || selectedUser).address) && (
                        <div className="users-modal-field users-modal-field-wide">
                          <label>Address</label>
                          <span>{formatAddress((userDetails || selectedUser).address)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {(selectedUser.roles || selectedUser.role || '').toLowerCase() === 'seller' && roleMetrics?.stores !== undefined && (
                    <div className="users-modal-section">
                      <h3>Stores ({roleMetrics.totalStores})</h3>
                      {roleMetrics.stores?.length === 0 ? (
                        <p className="users-metrics-empty">No stores found.</p>
                      ) : (
                        <>
                          <div className="users-metrics-summary">
                            <span><strong>{roleMetrics.totalOrders}</strong> total orders</span>
                            <span><strong>{formatCurrency(roleMetrics.totalRevenue)}</strong> total revenue</span>
                          </div>
                          <div className="users-stores-table-wrap">
                            <table className="users-stores-table">
                              <thead>
                                <tr>
                                  <th>Store</th>
                                  <th>Orders</th>
                                  <th>Revenue</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {roleMetrics.stores.map((s) => (
                                  <tr key={s.storeId}>
                                    <td>{s.storeName}</td>
                                    <td>{s.orderCount}</td>
                                    <td>{formatCurrency(s.revenue)}</td>
                                    <td>
                                      <button type="button" className="users-link-btn" onClick={() => { setSelectedUser(null); navigate(`/dashboard/stores/${s.storeId}`); }}>
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {roleMetrics.customers?.length > 0 && (
                            <div className="users-customers-section">
                              <h4>Customers / Buyers ({roleMetrics.customers.length})</h4>
                              <div className="users-stores-table-wrap">
                                <table className="users-stores-table">
                                  <thead>
                                    <tr>
                                      <th>Customer</th>
                                      <th>Email</th>
                                      <th>Orders</th>
                                      <th>Total Spent</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {roleMetrics.customers.map((c) => (
                                      <tr key={c.customerId}>
                                        <td>{c.name}</td>
                                        <td>{c.email || '—'}</td>
                                        <td>{c.orderCount}</td>
                                        <td>{formatCurrency(c.totalSpent)}</td>
                                        <td>
                                          <button type="button" className="users-link-btn" onClick={() => { setSelectedUser(null); navigate('/dashboard/orders', { state: { filterCustomer: c.customerId } }); }}>
                                            View orders
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {['customer', 'buyer'].includes((selectedUser.roles || selectedUser.role || '').toLowerCase()) && roleMetrics && (
                    <div className="users-modal-section">
                      <h3>Customer Activity &amp; Metrics</h3>
                      <div className="users-metrics-summary">
                        <span><strong>{roleMetrics.orderCount || 0}</strong> orders</span>
                        <span><strong>{formatCurrency(roleMetrics.totalSpent || 0)}</strong> total spent</span>
                      </div>
                      {roleMetrics.byStatus && Object.keys(roleMetrics.byStatus).length > 0 && (
                        <div className="users-metrics-status">
                          <span className="users-metrics-label">Order status:</span>
                          {Object.entries(roleMetrics.byStatus).map(([status, count]) => (
                            <span key={status} className="users-metrics-badge">{status}: {count}</span>
                          ))}
                        </div>
                      )}
                      {roleMetrics.topStores?.length > 0 && (
                        <div className="users-customer-detail-section">
                          <h4>Stores bought from most</h4>
                          <div className="users-stores-table-wrap">
                            <table className="users-stores-table">
                              <thead><tr><th>Store</th><th>Orders</th><th>Spent</th><th></th></tr></thead>
                              <tbody>
                                {roleMetrics.topStores.map((s) => (
                                  <tr key={s.storeId}>
                                    <td>{s.storeName}</td>
                                    <td>{s.orderCount}</td>
                                    <td>{formatCurrency(s.totalSpent)}</td>
                                    <td>
                                      <button type="button" className="users-link-btn" onClick={() => { setSelectedUser(null); navigate(`/dashboard/stores/${s.storeId}`); }}>View</button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {roleMetrics.topProducts?.length > 0 && (
                        <div className="users-customer-detail-section">
                          <h4>Products interested in most</h4>
                          <div className="users-stores-table-wrap">
                            <table className="users-stores-table">
                              <thead><tr><th>Product</th><th>Qty bought</th><th>Orders</th></tr></thead>
                              <tbody>
                                {roleMetrics.topProducts.map((p, i) => (
                                  <tr key={p.productName + i}>
                                    <td>{p.productName}</td>
                                    <td>{p.quantity}</td>
                                    <td>{p.orderCount}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {roleMetrics.deliveryUsage && Object.keys(roleMetrics.deliveryUsage).length > 0 && Object.keys(roleMetrics.deliveryUsage).some((k) => k !== 'unknown') && (
                        <div className="users-customer-detail-section">
                          <h4>Delivery option used most</h4>
                          <div className="users-metrics-status">
                            {Object.entries(roleMetrics.deliveryUsage)
                              .filter(([k]) => k !== 'unknown')
                              .sort((a, b) => b[1] - a[1])
                              .map(([method, count]) => (
                                <span key={method} className="users-metrics-badge">{method}: {count} orders</span>
                              ))}
                            {(roleMetrics.deliveryUsage.unknown || 0) > 0 && (
                              <span className="users-metrics-badge">Unknown: {roleMetrics.deliveryUsage.unknown} orders</span>
                            )}
                          </div>
                        </div>
                      )}
                      <button type="button" className="users-modal-btn-orders" onClick={handleViewOrders}>
                        View orders
                      </button>
                    </div>
                  )}
                  {(selectedUser.roles || selectedUser.role || '').toLowerCase() === 'driver' && roleMetrics && (
                    <div className="users-modal-section">
                      <h3>Driver Metrics</h3>
                      <div className="users-metrics-summary users-metrics-driver">
                        <span><strong>{roleMetrics.total || 0}</strong> total deliveries</span>
                        <span className="users-metric-success"><strong>{roleMetrics.completed || 0}</strong> completed</span>
                        <span className="users-metric-pending"><strong>{roleMetrics.pending || 0}</strong> pending</span>
                        <span className="users-metric-progress"><strong>{roleMetrics.inProgress || 0}</strong> in progress</span>
                      </div>
                    </div>
                  )}
                  <div className="users-modal-actions users-modal-actions-end">
                    {canUpdate && (
                      <button type="button" className="users-btn-edit" onClick={startEdit}>
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button type="button" className="users-btn-delete" onClick={() => setShowDeleteConfirm(true)}>
                        Delete
                      </button>
                    )}
                    {['customer', 'buyer'].includes((selectedUser.roles || selectedUser.role || '').toLowerCase()) && (
                      <button type="button" className="users-modal-btn-orders" onClick={handleViewOrders}>
                        View orders
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && selectedUser && (
        <div className="users-modal-overlay users-confirm-overlay" onClick={() => setShowDeleteConfirm(false)} role="dialog">
          <div className="users-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3>Delete User?</h3>
            <p>This will permanently delete {[selectedUser.fname, selectedUser.lname].filter(Boolean).join(' ') || selectedUser.email}. This action cannot be undone.</p>
            <div className="users-confirm-actions">
              <button type="button" className="users-btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button type="button" className="users-btn-delete" onClick={handleDelete} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
